import { createAction } from "@reduxjs/toolkit";
import _ from "lodash";
import axios from "axios";
import { takeEvery, call, put, select } from "redux-saga/effects";

import {
  CREATE_PODCAST_REQUEST,
  CREATING_PODCAST_SUCCEEDED,
  CREATING_PODCAST_FAILED,
  FETCH_DRAFT_PODCAST_REQUEST,
  FETCHING_DRAFT_PODCAST_SUCCEEDED,
  FETCHING_DRAFT_PODCAST_FAILED,
  EDIT_PODCAST_REQUEST,
  UPDATE_PODCAST_PROPERTY_REQUEST,
  VALIDATE_PODCAST_REQUEST,
  UPDATE_VALIDATION_ERRORS_FOR_PODCAST,
  UPDATE_PODCAST_REQUEST,
  UPDATING_PODCAST_SUCCEEDED,
  UPDATING_PODCAST_FAILED,
  INVALIDATE_PODCAST_CREATION_STATE,
  PUBLISHING_PODCAST_SUCCEEDED,
  PUBLISH_PODCAST_REQUEST,
  PUBLISHING_PODCAST_FAILED,
  INVALIDATE_PUBLISH_PODCAST,
  DELETE_PODCAST_REQUEST,
  DELETING_PODCAST_SUCCEEDED,
  DELETING_PODCAST_FAILED,
  INVALIDATE_PODCAST_DELETION_STATE,
  DISCARD_PODCAST_DRAFT_REQUEST,
  DISCARDING_PODCAST_DRAFT_FAILED,
  INVALIDATE_PODCAST_UPDATE_STATE,
  DISCARDING_PODCAST_DRAFT_SUCCEEDED,
  ADD_ASSET_TO_PODCAST,
  CREATING_PODCAST_EDITOR_CHAT_REQUEST,
  CREATING_PODCAST_EDITOR_CHAT_SUCCEEDED,
  CREATING_PODCAST_EDITOR_CHAT_FAILED,
  FETCH_PODCAST_TRANSCRIPTION_REQUEST,
  FETCHING_PODCAST_TTRANSCRIPTION_SUCCEEDED,
  FETCHING_PODCAST_TRANSCRIPTION_FAILED,
  QUICK_GENERATE_PODCAST_REQUEST,
  QUICK_GENERATING_PODCAST_SUCCEEDED,
  QUICK_GENERATING_PODCAST_FAILED,
  GENERATE_TRANSCRIPT_FOR_PODCAST_REQUEST,
  GENERATING_TRANSCRIPT_FOR_PODCAST_SUCCEEDED,
  GENERATING_TRANSCRIPT_FOR_PODCAST_FAILED,
  INVALIDATE_QUICK_GENERATING_PODCAST_STATE,
  INVALIDATE_TRANSCRIPT_FOR_PODCAST_STATE,
  SYNC_PODCAST_PROCESSING_STATE_REQUEST,
  SYNCING_PODCAST_PROCESSING_STATE_SUCCEEDED,
  SYNCING_PODCAST_PROCESSING_STATE_FAILED,
  FETCH_PUBLIC_PODCAST_REQUEST,
  FETCHING_PUBLIC_PODCAST_SUCCEEDED,
  FETCHING_PUBLIC_PODCAST_FAILED,
  FETCH_PODCAST_VOICE_PRESETS_REQUEST,
  FETCHING_PODCAST_VOICE_PRESETS_SUCCEEDED,
  FETCHING_PODCAST_VOICE_PRESETS_FAILED,
  GENERATING_AUDIO_FOR_PODCAST_FAILED,
  GENERATING_AUDIO_FOR_PODCAST_SUCCEEDED,
  GENERATE_AUDIO_FOR_PODCAST_REQUEST,
  INVALIDATE_AUDIO_FOR_PODCAST_STATE,
  FETCH_PODCAST_TRANSCRIPTION_FOR_EDIT_CHAT_REQUEST,
  FETCHING_PODCAST_TRANSCRIPTION_FOR_EDIT_CHAT_FAILED,
  FETCHING_PODCAST_TRANSCRIPTION_FOR_EDIT_CHAT_SUCCEEDED,
  INITIATE_PODCAST_EDITOR_REQUEST,
  INVALIDATE_DRAFT_PODCAST,
  SYNC_MICROLEARNING_PODCAST_STATE_REQUEST,
  SYNCING_MICROLEARNING_PODCAST_STATE_SUCCEEDED,
  SYNCING_MICROLEARNING_PODCAST_STATE_FAILED,
  FETCH_PUBLISHED_PODCAST_REQUEST,
  FETCHING_PUBLISHED_PODCAST_SUCCEEDED,
  FETCHING_PUBLISHED_PODCAST_FAILED,
  INVALIDATE_PUBLISHED_PODCAST,
} from "../actionTypes";

import {
  CreatePodcastFailed,
  CreatePodcastRequest,
  CreatePodcastResponse,
  DeletePodcastErrorResponse,
  DeletePodcastResponse,
  DiscardPodcastDraftErrorResponse,
  DiscardPodcastDraftResponse,
  FetchPodcastErrorResponse,
  FetchPodcastResponse,
  Podcast,
  PublishPodcastErrorResponse,
  PublishPodcastResponse,
  UpdatePodcastErrorResponse,
  UpdatePodcastPropertyRequest,
  UpdatePodcastRequest,
  UpdatePodcastResponse,
  UpdateValidationErrorsForPodcastRequest,
  AddAssetToPodcastRequest,
  CreatePodcastEditorChatRequest,
  CreatePodcastEditorChatResponse,
  CreatePodcastEditorChatErrorResponse,
  QuickGeneratePodcastRequest,
  GenerateTranscriptForPodcastRequest,
  GenerateTranscriptForPodcastResponse,
  GenerateTranscriptForPodcastErrorResponse,
  FetchPublicPodcastRequest,
  FetchPublicPodcastResponse,
  FetchPublicPodcastErrorResponse,
  FetchVoicePresetResponse,
  FetchVoicePresetErrorResponse,
  GenerateAudioForPodcastResponse,
  GenerateAudioForPodcastErrorResponse,
  FetchPodcastTranscriptionForEditChat,
  FetchPodcastTranscriptionForEditChatResponse,
  FetchPodcastTranscriptionForEditChatErrorResponse,
  GenerateAudioForPodcastRequest
} from "src/types/coaching/podcast";
import buildUrl from "build-url-ts";
import { validatePodcastRequest } from "src/pages/coaching/manage/podcasts/edit/helper";

// eslint-disable-next-line no-undef
const apiUrl = `${process.env.REACT_APP_COACHING_API_URL}`;
const PODCAST_API_URL = `${apiUrl}/api/1.0/podcasts`;

export const createPodcast = createAction<CreatePodcastRequest>(
  CREATE_PODCAST_REQUEST,
);
const creatingPodcastSucceeded = createAction<CreatePodcastResponse>(
  CREATING_PODCAST_SUCCEEDED,
);
const creatingPodcastFailed = createAction<CreatePodcastFailed>(
  CREATING_PODCAST_FAILED,
);
export const addAssetToPodcast = createAction<AddAssetToPodcastRequest>(ADD_ASSET_TO_PODCAST);

const createPodcastSaga = function* (
  action: ReturnType<typeof createPodcast>,
): any {
  const request = action.payload as CreatePodcastRequest;

  const createPodcastAPI = () => {
    return axios({
      method: "POST",
      url: `${PODCAST_API_URL}`,
      data: request,
    }).then((response) => response.data);
  };

  try {
    const response = yield call(createPodcastAPI);
    yield put(creatingPodcastSucceeded({ response: response }));
  } catch (error) {
    yield put(creatingPodcastFailed({ error: error }));
  }
};

export const fetchDraftPodcast = createAction<string>(
  FETCH_DRAFT_PODCAST_REQUEST,
);
const fetchingDraftPodcastSucceeded = createAction<FetchPodcastResponse>(
  FETCHING_DRAFT_PODCAST_SUCCEEDED,
);
const fetchingDraftPodcastFailed = createAction<FetchPodcastErrorResponse>(
  FETCHING_DRAFT_PODCAST_FAILED,
);

const fetchDraftPodcastSaga = function* (
  action: ReturnType<typeof fetchDraftPodcast>,
): any {
  const id = action.payload;

  const fetchPodcastApi = () => {
    return axios({
      method: "GET",
      url: `${PODCAST_API_URL}/${id}/draft`,
    }).then((response) => response.data);
  };

  try {
    const response = yield call(fetchPodcastApi);
    yield put(
      fetchingDraftPodcastSucceeded({
        response: response,
      }),
    );
  } catch (error) {
    yield put(fetchingDraftPodcastFailed({ id: id, error: error }));
  }
};


export const invalidatePublishedPodcast = createAction<string>(
  INVALIDATE_PUBLISHED_PODCAST,
);

export const fetchPublishedPodcast = createAction<string>(
  FETCH_PUBLISHED_PODCAST_REQUEST,
);
const fetchingPublishedPodcastSucceeded = createAction<FetchPodcastResponse>(
  FETCHING_PUBLISHED_PODCAST_SUCCEEDED,
);
const fetchingPublishedPodcastFailed = createAction<FetchPodcastErrorResponse>(
  FETCHING_PUBLISHED_PODCAST_FAILED,
);

const fetchPublishedPodcastSaga = function* (
  action: ReturnType<typeof fetchPublishedPodcast>,
): any {
  const id = action.payload;

  const fetchPodcastApi = () => {
    return axios({
      method: "GET",
      url: `${PODCAST_API_URL}/${id}/published`,
    }).then((response) => response.data);
  };

  try {
    const response = yield call(fetchPodcastApi);
    yield put(
      fetchingPublishedPodcastSucceeded({
        response: response,
      }),
    );
  } catch (error) {
    yield put(fetchingPublishedPodcastFailed({ id: id, error: error }));
  }
};

export const invalidateDraftPodcast = createAction<string>(INVALIDATE_DRAFT_PODCAST);

export const editPodcast = createAction<Podcast>(EDIT_PODCAST_REQUEST);
export const updatePodcastProperty = createAction<UpdatePodcastPropertyRequest>(
  UPDATE_PODCAST_PROPERTY_REQUEST,
);

export const validatePodcast = createAction<string>(VALIDATE_PODCAST_REQUEST);
export const updateValidationErrorsForPodcast =
  createAction<UpdateValidationErrorsForPodcastRequest>(
    UPDATE_VALIDATION_ERRORS_FOR_PODCAST,
  );

const validatePodcastSaga = function* (
  action: ReturnType<typeof validatePodcast>,
): any {
  const id = action.payload;
  const podcast = yield select((state) => state.coaching.podcasts.edit?.item);

  if (podcast && podcast.id === id) {
    const errors = validatePodcastRequest(podcast);
    yield put(
      updateValidationErrorsForPodcast({
        podcast: podcast.id!,
        errors: errors,
      }),
    );
  }
};

// update podcast
export const updatePodcast = createAction<UpdatePodcastRequest>(
  UPDATE_PODCAST_REQUEST,
);
const updatingPodcastSucceeded = createAction<UpdatePodcastResponse>(
  UPDATING_PODCAST_SUCCEEDED,
);
const updatingPodcastFailed = createAction<UpdatePodcastErrorResponse>(
  UPDATING_PODCAST_FAILED,
);

const updatePodcastSaga = function* (
  action: ReturnType<typeof updatePodcast>,
): any {
  const { id, request } = action.payload;

  const url = buildUrl(apiUrl, {
    path: `api/1.0/podcasts/${id}`,
  });

  const updatePodcastApi = () => {
    return axios({
      method: "PUT",
      url: url,
      data: request,
    }).then((response) => response.data);
  };

  try {
    const response = yield call(updatePodcastApi);
    yield put(updatingPodcastSucceeded({ response: response }));
    return response;
  } catch (error) {
    yield put(updatingPodcastFailed({ id: id, error: error }));
  }
};

export const invalidatePodcastUpdateState = createAction<void>(
  INVALIDATE_PODCAST_UPDATE_STATE,
);

export const invalidatePodcastCreationState = createAction<void>(
  INVALIDATE_PODCAST_CREATION_STATE,
);

// publish podcast
export const publishPodcast = createAction<string>(PUBLISH_PODCAST_REQUEST);
const publishingPodcastSucceeded = createAction<PublishPodcastResponse>(
  PUBLISHING_PODCAST_SUCCEEDED,
);
const publishingPodcastFailed = createAction<PublishPodcastErrorResponse>(
  PUBLISHING_PODCAST_FAILED,
);

const publishPodcastSaga = function* (
  action: ReturnType<typeof publishPodcast>,
): any {
  const id = action.payload;

  const url = buildUrl(apiUrl, {
    path: `api/1.0/podcasts/${id}/publish`,
  });

  const publishPodcastApi = () => {
    return axios({
      method: "POST",
      url: url,
    }).then((response) => response.data);
  };

  try {
    const response = yield call(publishPodcastApi);
    yield put(publishingPodcastSucceeded({ response: response }));
  } catch (error) {
    yield put(publishingPodcastFailed({ id: id, error: error }));
  }
};

export const invalidatePublishPodcast = createAction(INVALIDATE_PUBLISH_PODCAST);

// delete podcast
export const deletePodcast = createAction<string>(DELETE_PODCAST_REQUEST);
const deletingPodcastSucceeded = createAction<DeletePodcastResponse>(
  DELETING_PODCAST_SUCCEEDED,
);
const deletingPodcastFailed = createAction<DeletePodcastErrorResponse>(
  DELETING_PODCAST_FAILED,
);

const deletePodcastSaga = function* (
  action: ReturnType<typeof deletePodcast>,
): any {
  const id = action.payload;

  const url = buildUrl(apiUrl, {
    path: `api/1.0/podcasts/${id}`,
  });

  const deletePodcastApi = () => {
    return axios({
      method: "DELETE",
      url: url,
    }).then((response) => response.data);
  };

  try {
    const response = yield call(deletePodcastApi);
    yield put(deletingPodcastSucceeded({ id: id, response: response }));
  } catch (error) {
    yield put(deletingPodcastFailed({ id: id, error: error }));
  }
};

export const invalidatePodcastDeletionState = createAction<string>(
  INVALIDATE_PODCAST_DELETION_STATE,
);

// discard podcast draft
export const discardPodcastDraft = createAction<string>(
  DISCARD_PODCAST_DRAFT_REQUEST,
);

const discardingPodcastDraftSucceeded =
  createAction<DiscardPodcastDraftResponse>(DISCARDING_PODCAST_DRAFT_SUCCEEDED);
const discardingPodcastDraftFailed =
  createAction<DiscardPodcastDraftErrorResponse>(
    DISCARDING_PODCAST_DRAFT_FAILED,
  );

const discardPodcastDraftSaga = function* (
  action: ReturnType<typeof discardPodcastDraft>,
): any {
  const id = action.payload;

  const url = buildUrl(apiUrl, {
    path: `api/1.0/podcasts/${id}/draft`,
  });

  const discardPodcastApi = () => {
    return axios({
      method: "DELETE",
      url: url,
    }).then((response) => response.data);
  };

  try {
    const response = yield call(discardPodcastApi);
    yield put(discardingPodcastDraftSucceeded({ response: response }));
  } catch (error) {
    yield put(discardingPodcastDraftFailed({ id: id, error: error }));
  }
};

//create simulation editor chat
export const initiatePodcastEditor = createAction<string>(INITIATE_PODCAST_EDITOR_REQUEST);
export const invalidatePodcastEditor = createAction<string>(INVALIDATE_PODCAST_CREATION_STATE);

export const createPodcastEditorChat = createAction<CreatePodcastEditorChatRequest>(CREATING_PODCAST_EDITOR_CHAT_REQUEST);
const creatingPodcastEditorChatSucceeded = createAction<CreatePodcastEditorChatResponse>(CREATING_PODCAST_EDITOR_CHAT_SUCCEEDED);
const creatingPodcastEditorChatFailed = createAction<CreatePodcastEditorChatErrorResponse>(CREATING_PODCAST_EDITOR_CHAT_FAILED);

const createPodcastEditorChatSaga = function* (action: ReturnType<typeof createPodcastEditorChat>): any {
  const request = action.payload;

  const url = buildUrl(apiUrl, {
    path: `api/1.0/podcasts_editor/${request.podcast}`,
  });

  const data = {
    session: request.session,
    input: request.input,
  }

  const createPodcastEditorChatApi = () => {
    return axios({
      method: "POST",
      url: url,
      data: data,
    }).then((response) => response.data);
  };

  try {
    const response = yield call(createPodcastEditorChatApi);
    yield put(creatingPodcastEditorChatSucceeded(response));
  } catch (error) {
    yield put(creatingPodcastEditorChatFailed({
      podcast: request.podcast,
      session: request.session,
      error: error,
    }));
  }
};

export const fetchPodcastTranscriptionForEditChat = createAction<FetchPodcastTranscriptionForEditChat>(FETCH_PODCAST_TRANSCRIPTION_FOR_EDIT_CHAT_REQUEST);
const fetchingPodcastTranscriptionForEditChatSucceeded = createAction<FetchPodcastTranscriptionForEditChatResponse>(FETCHING_PODCAST_TRANSCRIPTION_FOR_EDIT_CHAT_SUCCEEDED);
const fetchingPodcastTranscriptionForEditChatFailed = createAction<FetchPodcastTranscriptionForEditChatErrorResponse>(FETCHING_PODCAST_TRANSCRIPTION_FOR_EDIT_CHAT_FAILED);

const fetchPodcastTranscriptionForEditChatSaga = function* (action: ReturnType<typeof fetchPodcastTranscriptionForEditChat>): any {
  const { podcast, session, chat } = action.payload as FetchPodcastTranscriptionForEditChat;

  const url = buildUrl(apiUrl, {
    path: `api/1.0/podcasts_editor/chats/${chat}/transcripts`,
  });

  const fetchTranscriptionAPI = () => {
    return axios({
      method: "GET",
      url: url,
    }).then((response) => response.data);
  };

  try {
    const response = yield call(fetchTranscriptionAPI);
    yield put(
      fetchingPodcastTranscriptionForEditChatSucceeded({
        podcast: podcast,
        session: session,
        chat: chat,
        response: response,
      }),
    );
  } catch (error) {
    yield put(fetchingPodcastTranscriptionForEditChatFailed({
      podcast: podcast,
      session: session,
      chat: chat,
      error: error
    }));
  }
};

export const fetchPodcastTranscription = createAction<string>(
  FETCH_PODCAST_TRANSCRIPTION_REQUEST,
);
const fetchingPodcastTranscriptionSucceeded = createAction<any>(
  FETCHING_PODCAST_TTRANSCRIPTION_SUCCEEDED,
);
const fetchingPodcastTranscriptionFailed = createAction<any>(
  FETCHING_PODCAST_TRANSCRIPTION_FAILED,
);

const fetchPodcastTranscriptionSaga = function* (
  action: ReturnType<typeof fetchPodcastTranscription>,
): any {
  const id = action.payload;

  const fetchTranscriptionAPI = () => {
    return axios({
      method: "GET",
      url: `${PODCAST_API_URL}/${id}/draft/transcripts`,
    }).then((response) => response.data);
  };

  try {
    const response = yield call(fetchTranscriptionAPI);
    yield put(
      fetchingPodcastTranscriptionSucceeded({
        id: id,
        response: response,
      }),
    );
  } catch (error) {
    yield put(fetchingPodcastTranscriptionFailed({ id: id, error: error }));
  }
};

export const generateTranscriptForPodcast =
  createAction<GenerateTranscriptForPodcastRequest>(
    GENERATE_TRANSCRIPT_FOR_PODCAST_REQUEST,
  );

const generatingTranscriptForPodcastSucceeded =
  createAction<GenerateTranscriptForPodcastResponse>(
    GENERATING_TRANSCRIPT_FOR_PODCAST_SUCCEEDED,
  );
const generatingTranscriptForPodcastFailed =
  createAction<GenerateTranscriptForPodcastErrorResponse>(
    GENERATING_TRANSCRIPT_FOR_PODCAST_FAILED,
  );

const generateTranscriptForPodcastSaga = function* (
  action: ReturnType<typeof generateTranscriptForPodcast>,
): any {
  const { id, generateAudio, draft } = action.payload as GenerateTranscriptForPodcastRequest;
  const generateTranscriptForPodcastAPI = () => {
    return axios({
      method: "GET",
      url: `${PODCAST_API_URL}/${id}/generate_transcript`,
      params: {
        generate_audio: generateAudio,
        draft: draft,
      },
    }).then((response) => response.data);
  };
  try {
    const response = yield call(generateTranscriptForPodcastAPI);
    yield put(
      generatingTranscriptForPodcastSucceeded({
        id: id,
        response: response,
      }),
    );

    return response;
  } catch (error) {
    yield put(generatingTranscriptForPodcastFailed({ id: id, error: error }));
  }
};

export const invalidateGenerateTranscriptionPodcastState = createAction<string>(INVALIDATE_TRANSCRIPT_FOR_PODCAST_STATE);

//Generate Audio for Podcast
export const generateAudioForPodcast = createAction<GenerateAudioForPodcastRequest>(GENERATE_AUDIO_FOR_PODCAST_REQUEST);
const generatingAudioForPodcastSucceeded = createAction<GenerateAudioForPodcastResponse>(GENERATING_AUDIO_FOR_PODCAST_SUCCEEDED);
const generatingAudioForPodcastFailed = createAction<GenerateAudioForPodcastErrorResponse>(GENERATING_AUDIO_FOR_PODCAST_FAILED);

const generateAudioForPodcastSaga = function* (
  action: ReturnType<typeof generateAudioForPodcast>,
): any {
  const { id, request } = action.payload as GenerateAudioForPodcastRequest;
  const generateAudioForPodcastAPI = () => {
    return axios({
      method: "POST",
      url: `${PODCAST_API_URL}/${id}/generate_audio`,
    }).then((response) => response.data);
  };

  try {
    let changesSynced = _.keys(request).length === 0;
    console.log("Request to generate audio for podcast", request);

    if (_.keys(request).length > 0) {
      const updateResponse = yield call(updatePodcastSaga, updatePodcast({ id, request: request }));
      if (updateResponse) {
        changesSynced = true;
        yield put(updatingPodcastSucceeded({ response: updateResponse }));
      }
    }

    if (changesSynced) {
      const response = yield call(generateAudioForPodcastAPI);
      yield put(
        generatingAudioForPodcastSucceeded({
          id: id,
          response: response,
        }),
      );
      return response;
    }

    return null;

  } catch (error) {
    yield put(generatingAudioForPodcastFailed({ id: id, error: error }));
  }
};

export const invalidateGenerateAudioPodcastState = createAction<string>(INVALIDATE_AUDIO_FOR_PODCAST_STATE);

//Quick generate podcast
export const quickGeneratePodcast = createAction<QuickGeneratePodcastRequest>(QUICK_GENERATE_PODCAST_REQUEST);
export const genertatingQuickPodcastSucceeded = createAction<any>(QUICK_GENERATING_PODCAST_SUCCEEDED);
const generatingQuickPodcastFailed = createAction<any>(QUICK_GENERATING_PODCAST_FAILED);

const quickGeneratePodcastSaga = function* (
  action: ReturnType<typeof quickGeneratePodcast>,
): any {
  const { id, request, generateAudio } = action.payload as QuickGeneratePodcastRequest;

  try {
    const updateResponse = yield call(updatePodcastSaga, updatePodcast({ id, request: request }));

    if (updateResponse) {
      yield put(generateTranscriptForPodcast({ id: id, generateAudio: generateAudio }));
    }
  } catch (error) {
    yield put(generatingQuickPodcastFailed({ id: id, error: error }));
  }
};

export const invalidateQuickGeneratePodcastState = createAction<string>(INVALIDATE_QUICK_GENERATING_PODCAST_STATE);

//Sync podcast processing state
export const syncPodcastProcessingState = createAction<string>(SYNC_PODCAST_PROCESSING_STATE_REQUEST);
const syncingPodcastProcessingStateSucceeded = createAction<FetchPodcastResponse>(SYNCING_PODCAST_PROCESSING_STATE_SUCCEEDED);
const syncingPodcastProcessingStateFailed = createAction<FetchPodcastErrorResponse>(SYNCING_PODCAST_PROCESSING_STATE_FAILED);

const syncPodcastProcessingStateSaga = function* (
  action: ReturnType<typeof syncPodcastProcessingState>,
): any {
  const id = action.payload;

  const fetchPodcastApi = () => {
    return axios({
      method: "GET",
      url: `${PODCAST_API_URL}/${id}/draft`,
    }).then((response) => response.data);
  };

  try {
    const response = yield call(fetchPodcastApi);
    yield put(
      syncingPodcastProcessingStateSucceeded({
        response: response,
      }),
    );
  } catch (error) {
    yield put(syncingPodcastProcessingStateFailed({ id: id, error: error }));
  }
};

//Public Podcast API
export const fetchPublicPodcast = createAction<FetchPublicPodcastRequest>(FETCH_PUBLIC_PODCAST_REQUEST);

const fetchPublicPodcastSucceeded = createAction<FetchPublicPodcastResponse>(FETCHING_PUBLIC_PODCAST_SUCCEEDED);
const fetchPublicPodcastFailed = createAction<FetchPublicPodcastErrorResponse>(FETCHING_PUBLIC_PODCAST_FAILED);

const fetchPublicPodcastSaga = function* (action: ReturnType<typeof fetchPublicPodcast>): any {
  const request = action.payload as FetchPublicPodcastRequest;
  const id = request.id;

  const fetchPublicPodcastApi = () => {
    return axios({
      method: "GET",
      url: `${apiUrl}/api/1.0/podcasts/public/${id}`,
    }).then((response) => response.data);
  };

  try {
    const response = yield call(fetchPublicPodcastApi);
    yield put(
      fetchPublicPodcastSucceeded({
        podcast: response.podcast,
        learningRecord: response.learningRecord
      }),
    );
  } catch (error) {
    yield put(fetchPublicPodcastFailed({ id: id, error: error }));
  }
};

//Fetch voice presets
export const fetchVoicePresets = createAction<string>(FETCH_PODCAST_VOICE_PRESETS_REQUEST);

const fetchingVoicePresetsSucceeded = createAction<FetchVoicePresetResponse>(FETCHING_PODCAST_VOICE_PRESETS_SUCCEEDED);
const fetchingVoicePresetsFailed = createAction<FetchVoicePresetErrorResponse>(FETCHING_PODCAST_VOICE_PRESETS_FAILED);

const fetchVoicePresetsSaga = function* (action: ReturnType<typeof fetchVoicePresets>): any {
  const podcastType = action.payload;

  const fetchVoicePresetsApi = () => {
    return axios({
      method: "GET",
      url: `${apiUrl}/api/1.0/podcasts/voice_presets?type=${podcastType}`,
    }).then((response) => response.data);
  };

  try {
    const response = yield call(fetchVoicePresetsApi);
    yield put(
      fetchingVoicePresetsSucceeded({
        type: podcastType,
        response: response,
      }),
    );
  } catch (error) {
    yield put(fetchingVoicePresetsFailed({ 
      type: podcastType, 
      error: error 
    }));
  }
};


//Sync podcast processing state
export const syncMicroLearningPodcastState = createAction<FetchPublicPodcastRequest>(SYNC_MICROLEARNING_PODCAST_STATE_REQUEST);
const syncingMicroLearningPodcastProcessingStateSucceeded = createAction<FetchPublicPodcastResponse>(SYNCING_MICROLEARNING_PODCAST_STATE_SUCCEEDED);
const syncingMicroLearningPodcastProcessingStateFailed = createAction<FetchPublicPodcastErrorResponse>(SYNCING_MICROLEARNING_PODCAST_STATE_FAILED);

const syncMicroLearningPodcastStateSaga = function* (
  action: ReturnType<typeof syncMicroLearningPodcastState>,
): any {
  const request = action.payload as FetchPublicPodcastRequest;
  const id = request.id;


  const fetchMicroLearningAPI = () => {
    return axios({
      method: "GET",
      url: `${apiUrl}/api/1.0/podcasts/public/${id}`,
    }).then((response) => response.data);
  };

  try {
    const response = yield call(fetchMicroLearningAPI);
    yield put(
      syncingMicroLearningPodcastProcessingStateSucceeded({
        podcast: response.podcast,
        learningRecord: response.learningRecord,
      }),
    );
  } catch (error) {
    yield put(syncingMicroLearningPodcastProcessingStateFailed({ id: id, error: error }));
  }
};


export default function* podcastsSaga() {
  yield takeEvery(CREATE_PODCAST_REQUEST, createPodcastSaga);
  yield takeEvery(FETCH_DRAFT_PODCAST_REQUEST, fetchDraftPodcastSaga);
  yield takeEvery(FETCH_PUBLISHED_PODCAST_REQUEST, fetchPublishedPodcastSaga);
  yield takeEvery(VALIDATE_PODCAST_REQUEST, validatePodcastSaga);
  yield takeEvery(UPDATE_PODCAST_REQUEST, updatePodcastSaga);
  yield takeEvery(PUBLISH_PODCAST_REQUEST, publishPodcastSaga);
  yield takeEvery(DELETE_PODCAST_REQUEST, deletePodcastSaga);
  yield takeEvery(DISCARD_PODCAST_DRAFT_REQUEST, discardPodcastDraftSaga);
  yield takeEvery(CREATING_PODCAST_EDITOR_CHAT_REQUEST, createPodcastEditorChatSaga);
  yield takeEvery(FETCH_PODCAST_TRANSCRIPTION_FOR_EDIT_CHAT_REQUEST, fetchPodcastTranscriptionForEditChatSaga);
  yield takeEvery(FETCH_PODCAST_TRANSCRIPTION_REQUEST, fetchPodcastTranscriptionSaga);
  yield takeEvery(QUICK_GENERATE_PODCAST_REQUEST, quickGeneratePodcastSaga);
  yield takeEvery(GENERATE_TRANSCRIPT_FOR_PODCAST_REQUEST, generateTranscriptForPodcastSaga);
  yield takeEvery(GENERATE_AUDIO_FOR_PODCAST_REQUEST, generateAudioForPodcastSaga);
  yield takeEvery(SYNC_PODCAST_PROCESSING_STATE_REQUEST, syncPodcastProcessingStateSaga);
  yield takeEvery(FETCH_PUBLIC_PODCAST_REQUEST, fetchPublicPodcastSaga);
  yield takeEvery(FETCH_PODCAST_VOICE_PRESETS_REQUEST, fetchVoicePresetsSaga);
  yield takeEvery(SYNC_MICROLEARNING_PODCAST_STATE_REQUEST, syncMicroLearningPodcastStateSaga);
}
